import { Head } from '@infl-ts/ui-advocate-components';
import { baseTheme as defaultTheme } from '@infl-ts/ui-advocate-theme';
import { DE$ } from '@infl-ts/util-datum';
import { O$, S$ } from '@infl-ts/util-fp-ts';
import { L$ } from '@infl-ts/util-monocle-ts';
import { stripExtraWords } from '@infl-ts/util-string-strip-extra-words';
import { isValidUrl } from '@infl-ts/util-url';
import { useDeviceType } from '@infl-ts/util-use-device-type';
import { alpha, createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import * as mediaQuery from 'css-mediaquery';
import { flow, identity, pipe } from 'fp-ts/function';
import * as Id from 'fp-ts/Identity';
import * as O from 'fp-ts/Option';
import * as Str from 'fp-ts/string';
import * as t from 'io-ts';
import * as L from 'monocle-ts/Lens';
import * as Opt from 'monocle-ts/Optional';
import useThemeChanger from '../../lib/use-theme-changer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var bannerLens = pipe(L.id(), L.prop('palette'), L.fromNullable, Opt.prop('other'), Opt.prop('banner'));
var primaryLens = pipe(L.id(), L.prop('palette'), L.fromNullable, Opt.prop('primary'));
var fontLens = pipe(L.id(), L.prop('typography'), L.filter(function (x) {
  return typeof x !== 'function' && !!x;
}), Opt.prop('fontFamily'));
var baselineStyleOverridesLens = pipe(L.id(), L.prop('components'), L$.withDefault({}), L.prop('MuiCssBaseline'), L$.withDefault({}), L.prop('styleOverrides'), L$.withDefault({}));
var useMediaQueryDefaultPropsLens = pipe(L.id(), L.prop('components'), L$.withDefault({}), L.prop('MuiUseMediaQuery'), L$.withDefault({}), L.prop('defaultProps'), L$.withDefault({}));
var PaletteColorWithContrastText = t.type({
  contrastText: t.string
});
var isGoogleFont = Str.includes('googleapis.com');

var prepareDefaultTheme = function prepareDefaultTheme(_ref) {
  var _ref$deviceType = _ref.deviceType,
      deviceType = _ref$deviceType === void 0 ? 'desktop' : _ref$deviceType;
  return function (theme) {
    return pipe(theme, useMediaQueryDefaultPropsLens.set({
      ssrMatchMedia: function ssrMatchMedia(query) {
        return {
          matches: mediaQuery.match(query, {
            width: deviceType === 'mobile' ? theme.breakpoints.values.xs : deviceType === 'tablet' ? theme.breakpoints.values.md : theme.breakpoints.values.lg
          })
        };
      }
    }));
  };
};

var convertToFullTheme = function convertToFullTheme(_ref2) {
  var bannerColorScheme = _ref2.bannerColorScheme,
      colorScheme = _ref2.colorScheme,
      customFontName = _ref2.customFontName,
      customFontUrl = _ref2.customFontUrl;
  return function (defaultTheme) {
    var _defaultTheme$palette, _defaultTheme$palette2;

    return pipe(defaultTheme, bannerColorScheme ? bannerLens.set(bannerColorScheme) : identity, colorScheme ? primaryLens.set({
      main: colorScheme,
      background: alpha(colorScheme, 0.08),
      contrastText: PaletteColorWithContrastText.is((_defaultTheme$palette = defaultTheme.palette) === null || _defaultTheme$palette === void 0 ? void 0 : _defaultTheme$palette.primary) ? (_defaultTheme$palette2 = defaultTheme.palette) === null || _defaultTheme$palette2 === void 0 ? void 0 : _defaultTheme$palette2.primary.contrastText : undefined
    }) : identity, customFontName && customFontUrl ? fontLens.set("".concat(customFontName, ", Proxima Nova, Open Sans, sans-serif")) : identity, customFontName && customFontUrl && !isGoogleFont(customFontUrl) ? baselineStyleOverridesLens.set({
      '@font-face': {
        fontFamily: "".concat(customFontName),
        fontStyle: 'normal',
        fontDisplay: 'swap',
        src: "url(".concat(customFontUrl, ")")
      }
    }) : identity);
  };
};

var googleFontPreload = function googleFontPreload(fontUrl) {
  return pipe(fontUrl, O.map(Str.replace('"', '')), O.map(stripExtraWords), O.chain(O.fromPredicate(isValidUrl)), O.chain(O.fromPredicate(isGoogleFont)), O.map(function (url) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("link", {
        rel: "preconnect",
        href: "https://fonts.googleapis.com"
      }), /*#__PURE__*/_jsx("link", {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: ""
      }), /*#__PURE__*/_jsx("link", {
        rel: "stylesheet",
        href: url
      })]
    });
  }), O.toNullable);
};

var ThemeProvider = function ThemeProvider(_ref3) {
  var themeConfig = _ref3.themeConfig,
      modes = _ref3.modes,
      children = _ref3.children;
  var deviceType = useDeviceType();

  var _useThemeChanger = useThemeChanger(modes.includes('superAdmin')),
      themeChanger = _useThemeChanger.themeChanger,
      customTheme = _useThemeChanger.customTheme;

  return pipe(themeConfig, DE$.getSuccessOrElse(O.none), O$.unify, O.map(function (conf) {
    return flow(convertToFullTheme(conf), function (theme) {
      return {
        theme: theme,
        fontUrl: O.fromNullable(conf.customFontUrl)
      };
    });
  }), O.getOrElse(function () {
    return function (theme) {
      return {
        theme: theme,
        fontUrl: O.none
      };
    };
  }), Id.ap(defaultTheme), S$.partialEvolve({
    theme: flow(createTheme, customTheme ? function (t) {
      return createTheme(t, customTheme);
    } : identity, prepareDefaultTheme({
      deviceType: deviceType
    }), createTheme)
  }), function (_ref4) {
    var theme = _ref4.theme,
        fontUrl = _ref4.fontUrl;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Head, {
        children: [/*#__PURE__*/_jsx("meta", {
          name: "theme-color",
          content: theme.palette.primary.main
        }), googleFontPreload(fontUrl)]
      }), /*#__PURE__*/_jsx(MuiThemeProvider, {
        theme: theme,
        children: children
      }), themeChanger]
    });
  });
};

export default ThemeProvider;